"use client";

import { Box, SxProps, Theme, Typography, Stack, alpha } from "@mui/material";
import React from "react";
import { deepmerge } from "@mui/utils";
import { sanitizeBasic } from "@/utils/sanitize-html";
import Image from "next/image";
import {Image as ImageType} from "@/types/dto/common/image";
import DynamicCtaButton from "@/components/common/dynamic-cta-button/dynamic-cta-button";
import {BasicInfoMultipleCtas} from "@/types/dto/common/basic-info";
import { DynamicCta, CtaType } from "@/types/dto/common/dynamic-cta";
import {LocationOnOutlined} from "@mui/icons-material";
import dynamicCtaButtonListFactory from "@/factories/common/dynamic-cta-button/dynamic-cta-button-factory";
import { SectionHeader } from "../../section-header";

export interface BasePageHeroSlotProps {
  container?: {
    sx?: SxProps<Theme>;
  };
  header?: {
    sx?: SxProps<Theme>;
  };
  logo?: {
    sx?: SxProps<Theme>;
  };
  backgroundImage?: {
    sx?: SxProps<Theme>;
  };
  sectionHeader?: {
    copySx?: SxProps<Theme>;
  };
}

interface BasePageHeroProps {
  imageSrc: string;
  imageAlt: string;
  dataParentContainerType: string;
  dataModuleName: string;
  moduleRankingPosition: string;
  pageTitle: string;
  testId?: string;
  slotProps?: BasePageHeroSlotProps;
  variant?: "text" | "logo";
  logoImage?: ImageType;
  basicInfoMultipleCtas?: BasicInfoMultipleCtas;
  ctaList?: DynamicCta<CtaType>[];
  locationText?: string;
  disclaimerText?: string;
}

const BasePageHero = ({
  imageSrc,
  imageAlt,
  pageTitle,
  dataParentContainerType,
  dataModuleName,
  moduleRankingPosition,
  testId = "base-page-hero",
  variant = "text",
  logoImage,
  slotProps,
  basicInfoMultipleCtas,
  ctaList,
  locationText,
  disclaimerText
}: BasePageHeroProps) => {
  if (variant === "text" && !pageTitle?.trim()) return null;
  if (variant === "logo" && !logoImage?.imageUrl?.trim()) return null;

  const mappedCtaList =
    basicInfoMultipleCtas &&
    "ctas" in basicInfoMultipleCtas &&
    Array.isArray(basicInfoMultipleCtas.ctas)
      ? dynamicCtaButtonListFactory(basicInfoMultipleCtas.ctas)
          .filter((cta): cta is DynamicCta<CtaType> => !!cta)
      : [];

  const resolvedCtaList = ctaList ?? mappedCtaList;

  return (
    <Box
      className="fluid-box"
      data-track="visibility"
      data-parent-container-type={dataParentContainerType}
      data-module-name={dataModuleName}
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
      data-testid={testId}
      component="section"
      sx={deepmerge(
        {
          position: "relative",
          display: "flex",
          minHeight: { xs: "23rem", md: "32rem", lg: "42rem" },
          padding: { xs: "4rem 2.4rem", lg: "6rem 10rem" },
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
          alignSelf: "stretch",
          overflow: "hidden",
          width: "100%"
        },
        { ...slotProps?.container?.sx }
      )}
    >
      <Image
        src={imageSrc}
        alt={imageAlt}
        fill
        style={{ objectFit: "cover" }}
        priority
        data-testid={`${testId}-bg-image`}
      />
      <Box
        sx={deepmerge(
          {
            position: "absolute",
            inset: 0,
            background: (theme) =>
              `linear-gradient(0deg, ${alpha(theme.palette.common.black, 0.4)} 0%, ${alpha(theme.palette.common.black, 0)} 100%)`
          },
          { ...slotProps?.backgroundImage?.sx }
        )}
        data-testid={`${testId}-overlay`}
      />

      <Stack
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: "2.4rem", lg: "3rem" },
          width: "100%"
        }}
      >
        {variant === "text" && (
          <>
            <Typography
              variant="d1"
              dangerouslySetInnerHTML={{ __html: sanitizeBasic(pageTitle) }}
              sx={deepmerge(
                {
                  position: "relative",
                  zIndex: 1,
                  color: "common.white",
                  textAlign: "center",
                  maxWidth: "814px",
                  width: "clamp(342px, 80vw, 814px)"
                },
                { ...slotProps?.header?.sx }
              )}
              data-testid={`${testId}-h1`}
            />
            <SectionHeader
              copyText={basicInfoMultipleCtas?.copyText}
              copyTestId={`${testId}-copy`}
              sx={{
                alignItems: "center",
                gap: { xs: "2.4rem", lg: "3rem" }
              }}
              copySx={deepmerge(
                {
                  color: "common.white",
                  textAlign: "center",
                  alignSelf: "center",
                  maxWidth: "814px",
                  fontSize:{xs:"1.6rem",md:"1.8rem"},
                  "& p": {
                    margin: "0rem"
                  }
                },
                { ...slotProps?.sectionHeader?.copySx }
              )}
            />
          </>
        )}

        {variant === "logo" && (
          <>
            {pageTitle.trim() && (
              <Typography
                variant="h1"
                sx={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  padding: 0,
                  margin: "-1px",
                  overflow: "hidden",
                  clip: "rect(0, 0, 0, 0)",
                  whiteSpace: "nowrap",
                  border: 0
                }}
                data-testid={`${testId}-h1`}
                dangerouslySetInnerHTML={{ __html: sanitizeBasic(pageTitle) }}
              />
            )}
            <Box
              sx={deepmerge(
                {
                  position: "relative",
                  height: "auto",
                  width: "auto",
                  maxWidth: {
                    xs: "29rem",
                    sm: "45rem",
                    lg: "70rem"
                  }
                },
                {
                  ...slotProps?.logo?.sx
                }
              )}
              data-testid={`${testId}-logo`}
            >
              {logoImage?.imageUrl && (
                <Image
                  src={logoImage?.imageUrl}
                  alt={logoImage?.altText ?? ""}
                  width={1120}
                  height={630}
                  style={{ maxWidth: "100%", width: "auto", height: "auto" }}
                  priority
                  data-testid={`${testId}-logo-image`}
                />
              )}
            </Box>
          </>
        )}

        {disclaimerText && (
          <Typography
            variant="subtitle1"
            data-testid={`${testId}-disclaimer-text`}
            sx={{
              "&::before": { content: "\"*\"", ariaHidden: "true" },
              color: "common.white",
              fontWeight: "600"
            }}
          >
            {disclaimerText}
          </Typography>
        )}

        {locationText && (
          <Stack
            sx={{
              gap: "5px",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: { xs: "center", lg: "flex-start" },
              width: "100%",
              maxWidth: "124rem"
            }}
            data-testid={`${testId}-location-stack`}
          >
            <LocationOnOutlined
              data-testid={`${testId}-location-pin`}
              sx={{ color: "common.white" }}
            />
            <Typography
              data-testid={`${testId}-location-text`}
              variant="h6"
              sx={{
                color: "common.white"
              }}
            >
              {locationText}
            </Typography>
          </Stack>
        )}

        {resolvedCtaList && resolvedCtaList.length > 0 && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2.4}
            sx={{ flexWrap: "wrap", justifyContent: "center" }}
            data-testid={`${testId}-ctas-stack`}
          >
            {resolvedCtaList.map((cta, index) => {
              // Build a descriptive key segment from CTA properties
              // as a fallback so repeated labels or URLs do not collide.
              let ctaKeyBase = `${cta.ctaType}-${cta.label?.replace(/\s+/g, "-").toLowerCase() || "cta"}`;
              if (
                (cta.ctaType === "internalLink" || cta.ctaType === "externalLink") &&
                "url" in cta.ctaData
              ) {
                ctaKeyBase = `${cta.ctaType}-${cta.ctaData.url}`;
              }
              return (
                <DynamicCtaButton
                  key={ctaKeyBase}
                  label={cta.label ?? ""}
                  ctaType={cta.ctaType}
                  ctaData={cta.ctaData}
                  testId={`${testId}-cta-${index}`}
                  variant={cta.variant ?? (index % 2 === 0 ? "outlined" : "contained")}
                  color={cta.color ?? "primary"}
                  targetIdSuffix="hero"
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default BasePageHero;
